/**
 * HabitTrackerWidget - Daily habit check-off with streaks and weekly progress
 */

import React, { useState, useMemo } from 'react';
import { Plus, Flame, Check, Loader2, Pencil, Trash2 } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import type { HabitTrackerWidgetConfig } from '../../types/widgets';
import type { HabitWithCompletions, HabitFrequency, HabitCreate, HabitUpdate } from '../../types';
import { useHabits } from '../../hooks/useHabits';
import { useGoals } from '../../hooks/useGoals';

interface HabitTrackerWidgetProps {
  config: HabitTrackerWidgetConfig;
  widgetId: string;
  windowId: string;
}

function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getWeekDates(): Date[] {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d);
  }
  return dates;
}

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function isCompletedOn(habit: HabitWithCompletions, dateStr: string): boolean {
  return habit.completions.some((c) => c.completed_date.startsWith(dateStr));
}

function FrequencyBadge({ habit }: { habit: HabitWithCompletions }) {
  if (habit.frequency_type === 'daily') return null;
  if (habit.frequency_type === 'weekly') {
    return <span className="text-xs text-gray-400 ml-1">weekly</span>;
  }
  return (
    <span className="text-xs text-gray-400 ml-1">
      {habit.week_completions}/{habit.times_per_week}
    </span>
  );
}

function StreakDisplay({ streak }: { streak: number }) {
  if (streak === 0) return null;
  return (
    <span className="flex items-center gap-0.5 text-xs text-orange-500">
      <Flame className="w-3 h-3" />
      {streak}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared habit form (used for both add and edit)
// ---------------------------------------------------------------------------

interface HabitFormData {
  name: string;
  description?: string;
  frequency_type: HabitFrequency;
  times_per_week: number;
  target_duration_minutes?: number;
  goal_id?: string | null;
}

interface HabitFormProps {
  initial?: { name: string; description?: string; frequency_type: HabitFrequency; times_per_week: number; target_duration_minutes?: number; goal_id?: string | null };
  submitLabel: string;
  onSubmit: (data: HabitFormData) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
}

function HabitForm({ initial, submitLabel, onSubmit, onCancel, onDelete }: HabitFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [frequency, setFrequency] = useState<HabitFrequency>(initial?.frequency_type ?? 'daily');
  const [timesPerWeek, setTimesPerWeek] = useState(
    initial ? (initial.frequency_type === 'times_per_week' ? initial.times_per_week : 4) : 4
  );
  const [duration, setDuration] = useState(initial?.target_duration_minutes?.toString() ?? '');
  const [goalId, setGoalId] = useState(initial?.goal_id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { goals } = useGoals();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        frequency_type: frequency,
        times_per_week: frequency === 'daily' ? 7 : frequency === 'weekly' ? 1 : timesPerWeek,
        target_duration_minutes: duration ? parseInt(duration, 10) : undefined,
        goal_id: goalId || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSubmitting(true);
    try {
      await onDelete?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Habit name..."
        className="w-full px-2 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
        autoFocus
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Notes (e.g. prefer mornings, 10 min minimum)..."
        rows={2}
        className="w-full px-2 py-1.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 resize-none"
      />
      <div className="flex items-center gap-2">
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as HabitFrequency)}
          className="text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
        >
          <option value="daily">Daily</option>
          <option value="times_per_week">X per week</option>
          <option value="weekly">Weekly</option>
        </select>
        {frequency === 'times_per_week' && (
          <input
            type="number"
            min={1}
            max={7}
            value={timesPerWeek}
            onChange={(e) => setTimesPerWeek(parseInt(e.target.value, 10) || 1)}
            className="w-14 text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          />
        )}
        <input
          type="number"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          placeholder="min"
          className="w-16 text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
        />
      </div>
      {goals.length > 0 && (
        <select
          value={goalId}
          onChange={(e) => setGoalId(e.target.value)}
          className="w-full text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
        >
          <option value="">No goal</option>
          {goals.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      )}
      <div className="flex gap-1 justify-between">
        <div>
          {onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className={`px-2 py-1 text-xs rounded ${
                confirmDelete
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30'
              }`}
            >
              <span className="flex items-center gap-1">
                <Trash2 className="w-3 h-3" />
                {confirmDelete ? 'Confirm delete' : 'Delete'}
              </span>
            </button>
          )}
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-1 text-xs rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {submitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Habit row — displays a single habit with edit button
// ---------------------------------------------------------------------------

interface HabitRowProps {
  habit: HabitWithCompletions;
  today: string;
  weekDateStrs: string[];
  showWeekGrid: boolean;
  showStreaks: boolean;
  isToggling: boolean;
  onToggle: (habitId: string, date: string) => void;
  onEdit: () => void;
}

function HabitRow({ habit, today, weekDateStrs, showWeekGrid, showStreaks, isToggling, onToggle, onEdit }: HabitRowProps) {
  const completedToday = isCompletedOn(habit, today);

  if (showWeekGrid) {
    return (
      <div className="px-2 py-1 group/row">
        <div className="grid gap-0.5 items-center" style={{ gridTemplateColumns: '1fr repeat(7, minmax(20px, 24px))' }}>
          <div className="flex items-center gap-1 min-w-0">
            <button
              onClick={onEdit}
              className="text-sm text-gray-800 dark:text-gray-200 truncate hover:text-blue-600 dark:hover:text-blue-400 text-left"
              title="Edit habit"
            >
              {habit.name}
            </button>
            {habit.target_duration_minutes && (
              <span className="text-xs text-gray-400 flex-shrink-0">{habit.target_duration_minutes}m</span>
            )}
            <FrequencyBadge habit={habit} />
            {showStreaks && <StreakDisplay streak={habit.current_streak} />}
            <button
              onClick={onEdit}
              className="opacity-0 group-hover/row:opacity-100 p-0.5 text-gray-400 hover:text-blue-500 flex-shrink-0 transition-opacity"
              title="Edit habit"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </div>
          {weekDateStrs.map((dateStr) => {
            const completed = isCompletedOn(habit, dateStr);
            const isFuture = dateStr > today;
            const isToday = dateStr === today;
            return (
              <button
                key={dateStr}
                onClick={() => !isFuture && onToggle(habit.id, dateStr)}
                disabled={isFuture || isToggling}
                className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                  completed
                    ? 'bg-green-500 text-white'
                    : isFuture
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : isToday
                        ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                        : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                title={`${habit.name} - ${dateStr}`}
              >
                {completed && <Check className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Non-grid (simple list) mode
  return (
    <div className="px-2 py-1 group/row">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggle(habit.id, today)}
          disabled={isToggling}
          className="flex-shrink-0"
        >
          <div
            className={`w-5 h-5 rounded flex items-center justify-center ${
              completedToday
                ? 'bg-green-500 text-white'
                : 'border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400'
            }`}
          >
            {completedToday && <Check className="w-3.5 h-3.5" />}
          </div>
        </button>
        <button
          onClick={onEdit}
          className={`text-sm flex-1 text-left hover:text-blue-600 dark:hover:text-blue-400 ${
            completedToday ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-gray-200'
          }`}
          title="Edit habit"
        >
          {habit.name}
        </button>
        {habit.target_duration_minutes && (
          <span className="text-xs text-gray-400">{habit.target_duration_minutes}m</span>
        )}
        <FrequencyBadge habit={habit} />
        {showStreaks && <StreakDisplay streak={habit.current_streak} />}
        <button
          onClick={onEdit}
          className="opacity-0 group-hover/row:opacity-100 p-0.5 text-gray-400 hover:text-blue-500 flex-shrink-0 transition-opacity"
          title="Edit habit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly habit row
// ---------------------------------------------------------------------------

interface WeeklyHabitRowProps {
  habit: HabitWithCompletions;
  today: string;
  showStreaks: boolean;
  isToggling: boolean;
  onToggle: (habitId: string, date: string) => void;
  onEdit: () => void;
}

function WeeklyHabitRow({ habit, today, showStreaks, isToggling, onToggle, onEdit }: WeeklyHabitRowProps) {
  const completedThisWeek = habit.week_completions > 0;
  return (
    <div className="px-2 py-1 group/row">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggle(habit.id, today)}
          disabled={isToggling}
          className="flex-shrink-0"
        >
          <div
            className={`w-5 h-5 rounded flex items-center justify-center ${
              completedThisWeek
                ? 'bg-green-500 text-white'
                : 'border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400'
            }`}
          >
            {completedThisWeek && <Check className="w-3.5 h-3.5" />}
          </div>
        </button>
        <button
          onClick={onEdit}
          className={`text-sm flex-1 text-left hover:text-blue-600 dark:hover:text-blue-400 ${
            completedThisWeek ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-gray-200'
          }`}
          title="Edit habit"
        >
          {habit.name}
        </button>
        <span className="text-xs text-gray-400">weekly</span>
        {showStreaks && <StreakDisplay streak={habit.current_streak} />}
        <button
          onClick={onEdit}
          className="opacity-0 group-hover/row:opacity-100 p-0.5 text-gray-400 hover:text-blue-500 flex-shrink-0 transition-opacity"
          title="Edit habit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export function HabitTrackerWidget({ config, widgetId, windowId }: HabitTrackerWidgetProps) {
  const { habits, isLoading, toggleCompletion, createHabit, updateHabit, deleteHabit } = useHabits(!config.showInactive);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const today = useMemo(() => formatDate(new Date()), []);
  const weekDates = useMemo(() => getWeekDates(), []);
  const weekDateStrs = useMemo(() => weekDates.map(formatDate), [weekDates]);
  const todayIdx = useMemo(() => weekDateStrs.indexOf(today), [weekDateStrs, today]);

  const showWeekGrid = config.showWeekGrid !== false;
  const showStreaks = config.showStreaks !== false;

  const handleToggle = async (habitId: string, date: string) => {
    setTogglingId(habitId);
    try {
      await toggleCompletion({ habitId, date });
    } finally {
      setTogglingId(null);
    }
  };

  const handleAddHabit = async (data: HabitFormData) => {
    await createHabit(data as HabitCreate);
    setShowAddForm(false);
  };

  const handleUpdateHabit = async (habitId: string, data: HabitFormData) => {
    await updateHabit({ habitId, update: data as HabitUpdate });
    setEditingId(null);
  };

  const handleDeleteHabit = async (habitId: string) => {
    await deleteHabit(habitId);
    setEditingId(null);
  };

  const dailyHabits = habits.filter(
    (h) => h.frequency_type === 'daily' || h.frequency_type === 'times_per_week'
  );
  const weeklyHabits = habits.filter((h) => h.frequency_type === 'weekly');

  const habitsContent = (
      <div className="flex flex-col h-full">
        {isLoading ? (
          <div className="flex items-center justify-center h-20 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : habits.length === 0 && !showAddForm ? (
          <div className="flex flex-col items-center justify-center flex-1 text-gray-400 p-4">
            <p className="text-sm mb-2">No habits yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600"
            >
              <Plus className="w-3 h-3" />
              Add your first habit
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            {/* Week grid header */}
            {showWeekGrid && habits.length > 0 && (
              <div className="grid gap-0.5 px-2 pt-2 pb-1" style={{ gridTemplateColumns: '1fr repeat(7, minmax(20px, 24px))' }}>
                <div />
                {DAY_LABELS.map((label, i) => (
                  <div
                    key={i}
                    className={`text-center text-xs font-medium ${
                      i === todayIdx ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}

            {/* Daily / times_per_week habits */}
            {dailyHabits.map((habit) =>
              editingId === habit.id ? (
                <HabitForm
                  key={habit.id}
                  initial={habit}
                  submitLabel="Save"
                  onSubmit={(data) => handleUpdateHabit(habit.id, data)}
                  onCancel={() => setEditingId(null)}
                  onDelete={() => handleDeleteHabit(habit.id)}
                />
              ) : (
                <HabitRow
                  key={habit.id}
                  habit={habit}
                  today={today}
                  weekDateStrs={weekDateStrs}
                  showWeekGrid={showWeekGrid}
                  showStreaks={showStreaks}
                  isToggling={togglingId === habit.id}
                  onToggle={handleToggle}
                  onEdit={() => setEditingId(habit.id)}
                />
              )
            )}

            {/* Weekly habits */}
            {weeklyHabits.length > 0 && (
              <>
                {dailyHabits.length > 0 && (
                  <div className="px-2 py-1">
                    <div className="border-t border-gray-200 dark:border-gray-700" />
                  </div>
                )}
                {weeklyHabits.map((habit) =>
                  editingId === habit.id ? (
                    <HabitForm
                      key={habit.id}
                      initial={habit}
                      submitLabel="Save"
                      onSubmit={(data) => handleUpdateHabit(habit.id, data)}
                      onCancel={() => setEditingId(null)}
                      onDelete={() => handleDeleteHabit(habit.id)}
                    />
                  ) : (
                    <WeeklyHabitRow
                      key={habit.id}
                      habit={habit}
                      today={today}
                      showStreaks={showStreaks}
                      isToggling={togglingId === habit.id}
                      onToggle={handleToggle}
                      onEdit={() => setEditingId(habit.id)}
                    />
                  )
                )}
              </>
            )}

            {/* Inline add button */}
            {!showAddForm && habits.length > 0 && (
              <div className="px-2 py-1">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 p-1"
                >
                  <Plus className="w-3 h-3" />
                  Add habit
                </button>
              </div>
            )}
          </div>
        )}

        {showAddForm && (
          <HabitForm
            submitLabel="Add"
            onSubmit={handleAddHabit}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
  );

  return (
    <>
      <WidgetWrapper
        title={config.title || 'Habits'}
        widgetId={widgetId}
        windowId={windowId}
        onSettings={() => setShowAddForm((prev) => !prev)}
        onHeaderClick={() => setExpanded(true)}
      >
        {habitsContent}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title={config.title || 'Habits'} size="xl">
          <div className="max-h-[80vh] overflow-auto">{habitsContent}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
